import trimEnd from 'lodash/trimEnd'
import { MIN_SCALE, MAX_SCALE } from './Map'
import { high, meetingRoom } from './utils'
import drawText from './drawText'

import inView from './inView'
import getSpaceRadius from './getSpaceRadius'
import { scaleLinear } from 'd3-scale'
import drawArc from './drawArc'
import meetingRoomImage from '../../../assets/meetingRoom.png'
import diamondImage from '../../../assets/diamond.png'
import splitSpaceCode from './splitSpaceCode'
import { low, medium, superLow } from '@utils'
const mrImage = new Image()
mrImage.src = meetingRoomImage

const spaceImage = new Image()
spaceImage.src = diamondImage

const drawImage = ({ x, y, r, context, selected, image, status }) => {
  let color = '#51b149'
  if (status === 'occupied') {
    color = '#F9C75D'
  }

  if (r > 10) {
    // Gray shadow
    drawArc({
      cx: x,
      cy: y,
      r: 1.2 * r,
      start: 0,
      end: 2 * Math.PI,
      fillStyle: '#ddd',
      context,
    })

    // White border
    drawArc({
      cx: x,
      cy: y,
      r: 1.1 * r,
      start: 0,
      end: 2 * Math.PI,
      fillStyle: 'white',
      context,
    })
  }

  // Green background

  drawArc({
    cx: x,
    cy: y,
    r,
    start: 0,
    end: 2 * Math.PI,
    fillStyle: color,
    context,
  })

  context.drawImage(image, x - 0.9 * r, y - 0.9 * r, 1.8 * r, 1.8 * r)

  if (selected) {
    // Draw green circle
    drawArc({
      cx: x,
      cy: y,
      r: 1.5 * r,
      start: 0,
      end: 2 * Math.PI,
      strokeStyle: '#48ad48',
      lineWidth: Math.min(r / 3, 4),
      context,
    })
  }
}

const cropText = ({ t, name, density }) => {
  let maxScale = MAX_SCALE / 5
  if (high(density)) {
    if (t.k < 2) {
      return
    }
    maxScale = MAX_SCALE / 1.5
  }

  const textTrimScale = scaleLinear()
    .domain([MIN_SCALE, maxScale / 3, maxScale])
    .range([7, 10, 30])
    .clamp(true)

  let text = name
  if (text.length > textTrimScale(t.k)) {
    text = `${trimEnd(name.substr(0, textTrimScale(t.k)))}...`
  }

  return text
}

const drawName = ({ x, y, r, t, context, center, name, density, fontSize }) => {
  if (context.globalAlpha <= 0) {
    return
  }

  if (name) {
    const fillStyle = '#333'

    name
      .trim()
      .split(/\n/)
      .map((token, i) =>
        drawText({
          x,
          y: 1.6 * r + y + i * fontSize,
          text: cropText({ name: token.trim(), t, density }),
          center,
          fillStyle,
          context,
        })
      )
  }
}

const drawSpaceCode = (props) => {
  const { context, x, y, r, t, fontSize, code, preview, density, maxScale, force } = props
  let breakPoint = maxScale / 2

  if (density === 0) {
    breakPoint = 1
  } else if (superLow(density)) {
    breakPoint = 2.5
  } else if (low(density)) {
    breakPoint = maxScale / 6
  } else if (medium(density)) {
    breakPoint = maxScale / 3
  }
  const meetingRoomSpaceCode = splitSpaceCode(code)
  if (force?.isSpaceCodesEnabled || t.k >= breakPoint || preview) {
    meetingRoomSpaceCode?.forEach((code, i) => {
      drawText({
        x,
        y: y - i * fontSize - (force?.isCirclesEnabled ? 1.2 * r : 0),
        text: code,
        fillStyle: '#333',
        fontWeight: '500',
        textBaseline: 'bottom',
        fontSize,
        context,
        center: true,
      })
    })
  }
}

const Space = ({
  context,
  space,
  dx,
  dy,
  maxScale,
  xScale,
  yScale,
  rScale,
  transform: t,
  selected,
  filtered,
  mobile,
  screen,
  fontSize,
  preview,
  draggedItem,
  interactive,
  status,
  print,
  mapViewSettings,
  force = {
    isCirclesEnabled: true,
    isSpaceCodesEnabled: false,
    isEmployeeNameEnabled: false,
    isPhoneNumberEnabled: false,
    isOtherSpacesIconsEnabled: true,
    isOtherSpacesNamesEnabled: true,
  },
}) => {
  const x = dx * t.k + xScale(space.x)
  const y = dy * t.k + yScale(space.y)
  let r = getSpaceRadius({ space, k: t.k, rScale, filtered, maxScale })

  if (print && selected) {
    r = Math.min(25, r * 1.5)
  }

  if (t.k >= 3 && !inView({ x, y, screen })) {
    return
  }

  context.globalAlpha = 1

  if (filtered || preview || draggedItem) {
    context.globalAlpha = 0.5
  }

  if (mobile && interactive) {
    drawSpace({
      context,
      x,
      y,
      r,
      t,
      selected,
      dotColor: getColorByStatus(space.status),
      density: space.density,
      force,
      mobile: true,
      interactive: true,
      disabled,
    })
  }

  if (force.isOtherSpacesIconsEnabled && !(mobile && interactive)) {
    drawImage({
      context,
      x,
      y,
      r,
      t,
      selected,
      status,
      image: meetingRoom(space.spaceType) ? mrImage : spaceImage,
    })
  }

  if (force.isOtherSpacesNamesEnabled) {
    let nameDrawConfig = {
      context,
      x,
      y,
      r,
      t,
      fontSize: fontSize,
      name: space.name || space.code,
      density: space.density,
    }

    if (meetingRoom(space.spaceType)) {
      nameDrawConfig = {
        ...nameDrawConfig,
        y: y + (force.isOtherSpacesIconsEnabled ? 1.5 * r : 0) + fontSize / 2,
        center: true,
      }
    } else {
      nameDrawConfig = {
        ...nameDrawConfig,
        x: x + (force.isOtherSpacesIconsEnabled ? 1.5 * r : 0),
        center: !force.isOtherSpacesIconsEnabled,
      }
    }

    drawName(nameDrawConfig)

    drawSpaceCode({
      context,
      x,
      y,
      r,
      t,
      fontSize,
      maxScale,
      preview,
      code: space.code,
      density: space.density,
      force,
    })
  }
  context.globalAlpha = 1
}

export default Space
